import { assertValue } from '@shared/helpers'
import { AuthenticateResponse, ErrorCode, ErrorResponse } from '@shared/types'
import { authenticate } from '@ui-common/api/endpoints'
import { PromptCancelledEvent, PromptConfirmedEvent } from '@ui-common/state/types'
import { StorageKey } from '@ui-common/types'
import {
  AuthCallbackResult,
  cleanUpAuthQueryParams,
  debugLog,
  getLanguage,
  isCancelledCallbackResult,
  isErrorResponse,
  parseAuthCode,
  setAuthorizationHeaderBearerToken,
  timestamp,
} from '@ui-common/utils'
import { removeSessionStorageItem, setSessionStorageItem } from '@ui-common/utils/storage'
import { navigateToExternalUrl } from '@ui-components/utilities/navigation'
import axios from 'axios'
import { assign, ErrorActorEvent, fromPromise, setup } from 'xstate'

import { AuthenticateInput } from './types'

type AbortEvent = { type: 'ABORT' }
interface AuthenticateStepContext {
  authUrl: string
  authCode: string
  error?: ErrorResponse<ErrorCode>
}

export type AuthenticateStepEvent =
  | PromptCancelledEvent
  | PromptConfirmedEvent
  | AbortEvent
  | { type: 'AUTHENTICATE' }
  | { type: 'PARSE_AUTH_CODE' }

export const GET_AUTH_URL = 'getAuthUrl'
export const GET_AUTH_CODE = 'getAuthCode'
export const PARSE_AUTH_CODE = 'parseAuthCode'
export const VERIFY_AUTH_CODE = 'verifyAuthCode'
export const AUTH_ERROR = 'authError'
export const AUTH_CANCELLED = 'authCancelled'
export const END = 'end'

export const authenticateStepMachine = (
  { verifyAuthCodeCallback, onExit, onAuthenticated, onAuthenticationCancelled, authMode }: AuthenticateInput,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) =>
  setup({
    types: {} as {
      context: AuthenticateStepContext
      events: AuthenticateStepEvent
      input: AuthenticateStepContext
    },
    actors: {
      getAuthUrl: fromPromise((): Promise<AuthenticateResponse> => {
        debugLog('Service: authenticate')
        const lang = getLanguage()
        return authenticate(lang, authMode)
      }),
      parseAuthCode: fromPromise((): Promise<AuthCallbackResult> => {
        debugLog('Service: parseAuthCode')
        return parseAuthCode()
      }),
      verifyAuthCode: fromPromise(({ input }: { input: string }) => {
        debugLog('Service: verifyAuthCode')
        return verifyAuthCodeCallback(input)
      }),
    },
    actions: {
      storeAuthUrl: assign((_, authUrl: string) => {
        debugLog('Action: Store auth url')
        return {
          authUrl,
        }
      }),
      storeAuthCode: assign((_, authCode?: string) => {
        assertValue(authCode, 'authCode')
        debugLog('Action: Store auth code')
        return {
          authCode,
        }
      }),
      exit: () => onExit(),
      invokeAuthCancelCallback: () => {
        onAuthenticationCancelled?.()
      },
      proceedToNextStep: (_, authToken: string) => onAuthenticated(authToken),
      setAuthorizationHeaderBearerToken: (_, authToken: string) => {
        debugLog('Action: setAuthorizationHeaderBearerToken')
        setAuthorizationHeaderBearerToken(axios, authToken)
      },
      cleanupAuthParams: () => {
        debugLog('Action: cleanupAuthParam')
        removeSessionStorageItem(StorageKey.AuthenticationRedirected)
        cleanUpAuthQueryParams()
      },
      redirectToAuth: (_, authUrl: string) => {
        debugLog('Action: redirectToAuth')
        setSessionStorageItem(StorageKey.AuthenticationRedirected, timestamp())
        navigateToExternalUrl(authUrl)
      },
      storeError: assign((_, error: unknown) => {
        debugLog(error)
        return { error: isErrorResponse(error) ? error : undefined }
      }),
    },
    guards: {
      authCodeWasFound: (_, authCode?: string) => {
        const result = !!authCode
        debugLog(`Guard: authCodeWasFound: ${result}`)
        return result
      },
      isCancelledByUser: (_, data: AuthCallbackResult): boolean => isCancelledCallbackResult(data),
      navigatedBack: (_, data: AuthCallbackResult) => {
        const result = !!data.isBackNavigation
        debugLog(`Guard: navigatedBack: ${result}`)
        return result
      },
      isCancelledOrBackNavigation: (_, data: AuthCallbackResult) => {
        const result = !!data.isBackNavigation || isCancelledCallbackResult(data)
        debugLog(`Guard: isCancelledOrBackNavigation: ${result}`)
        return result
      },
      hasAuthCancelledCallback: () => {
        const result = !!onAuthenticationCancelled
        debugLog(`Guard: hasAuthCancelledCallback: ${result}`)
        return result
      },
    },
  }).createMachine({
    id: 'authenticateStepMachine',
    context: ({ input }) => input,
    initial: GET_AUTH_URL,
    states: {
      [GET_AUTH_URL]: {
        invoke: {
          src: 'getAuthUrl',
          onDone: {
            actions: { type: 'storeAuthUrl', params: ({ event }) => event.output.authUrl },
            target: GET_AUTH_CODE,
          },
          onError: {
            target: AUTH_ERROR,
          },
        },
      },
      [GET_AUTH_CODE]: {
        entry: { type: 'redirectToAuth', params: ({ context }) => context.authUrl },
        on: {
          PARSE_AUTH_CODE,
        },
      },
      [PARSE_AUTH_CODE]: {
        invoke: {
          src: 'parseAuthCode',
          onDone: {
            guard: { type: 'authCodeWasFound', params: ({ event }) => event.output.authCode },
            target: VERIFY_AUTH_CODE,
            actions: [
              {
                type: 'storeAuthCode',
                params: ({ event }) => event.output.authCode,
              },
              { type: 'cleanupAuthParams' },
            ],
          },
          onError: [
            {
              guard: { type: 'isCancelledOrBackNavigation', params: ({ event }) => event.error as AuthCallbackResult },
              actions: [{ type: 'cleanupAuthParams' }],
              target: AUTH_CANCELLED,
            },
            {
              target: AUTH_ERROR,
              actions: [
                { type: 'cleanupAuthParams' },
                {
                  type: 'storeError',
                  params: ({ event }: { event: ErrorActorEvent }) => event.error,
                },
              ],
            },
          ],
        },
      },
      [AUTH_CANCELLED]: {
        id: AUTH_CANCELLED,
        always: [
          {
            guard: { type: 'hasAuthCancelledCallback' },
            actions: [{ type: 'invokeAuthCancelCallback' }],
            target: END,
          },
        ],
        on: {
          AUTHENTICATE: GET_AUTH_URL,
          ABORT: { actions: { type: 'exit' } },
        },
      },
      [AUTH_ERROR]: {
        id: AUTH_ERROR,
        on: {
          AUTHENTICATE: GET_AUTH_URL,
          ABORT: { actions: { type: 'exit' } },
        },
      },
      [VERIFY_AUTH_CODE]: {
        invoke: {
          src: 'verifyAuthCode',
          input: ({ context }) => context.authCode,
          onDone: {
            actions: [
              {
                type: 'setAuthorizationHeaderBearerToken',
                params: ({ event }) => event.output.authToken,
              },
              {
                type: 'proceedToNextStep',
                params: ({ event }) => event.output.authToken,
              },
            ],
          },
          onError: {
            target: AUTH_ERROR,
            actions: {
              type: 'storeError',
              params: ({ event }: { event: ErrorActorEvent }) => event.error,
            },
          },
        },
      },
      [END]: {
        id: END,
        type: 'final',
      },
    },
  })
